import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  ClipboardList,
  Salad,
  UtensilsCrossed,
  IceCreamCone,
  Copy,
  Share2,
  RotateCcw,
  Plus,
  LogIn,
  CheckCircle2,
  Circle,
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SYNC_INTERVAL_MS = 3000;

const COURSE_META = [
  { key: "starter", label: "Starter", icon: Salad },
  { key: "main", label: "Main", icon: UtensilsCrossed },
  { key: "dessert", label: "Dessert", icon: IceCreamCone },
];

// ---- Setup card: create new session or join existing ----
const TallySetup = ({ onSession }) => {
  const navigate = useNavigate();
  const [tableCount, setTableCount] = useState("10");
  const [name, setName] = useState("Dinner Service");
  const [joinCode, setJoinCode] = useState("");
  const [creating, setCreating] = useState(false);
  const [joining, setJoining] = useState(false);

  const create = async () => {
    const count = parseInt(tableCount);
    if (!count || count < 1 || count > 200) {
      toast.error("Enter a table count between 1 and 200");
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/tally/sessions`, {
        name: name.trim() || "Dinner Service",
        table_count: count,
      });
      onSession(res.data);
      navigate(`/execution/tally/${res.data.code}`, { replace: true });
      toast.success(`Tally created — code ${res.data.code}`);
    } catch (e) {
      toast.error("Could not create tally");
    } finally {
      setCreating(false);
    }
  };

  const join = async () => {
    const code = joinCode.trim().toUpperCase();
    if (code.length !== 6) {
      toast.error("Codes are 6 characters");
      return;
    }
    setJoining(true);
    try {
      const res = await axios.get(`${API}/tally/sessions/${code}`);
      onSession(res.data);
      navigate(`/execution/tally/${code}`, { replace: true });
    } catch (e) {
      toast.error("Tally not found — check the code");
    } finally {
      setJoining(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto grid md:grid-cols-2 gap-6">
      <Card className="tactical-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg uppercase">
            <Plus className="h-5 w-5" />
            New Tally
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="label-tactical" htmlFor="tally-name">Service Name</Label>
            <Input
              id="tally-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Saturday Gala Dinner"
              className="tactical-input"
              data-testid="tally-name-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="label-tactical" htmlFor="tally-tables">Number of Tables</Label>
            <Input
              id="tally-tables"
              type="number"
              min="1"
              max="200"
              value={tableCount}
              onChange={(e) => setTableCount(e.target.value)}
              className="tactical-input"
              data-testid="tally-table-count-input"
            />
          </div>
          <Button
            onClick={create}
            disabled={creating}
            className="tactical-btn w-full"
            data-testid="tally-create-button"
          >
            {creating ? "Creating..." : "Create Tally"}
          </Button>
        </CardContent>
      </Card>

      <Card className="tactical-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg uppercase">
            <LogIn className="h-5 w-5" />
            Join Existing
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="label-tactical" htmlFor="tally-join">6-Character Code</Label>
            <Input
              id="tally-join"
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
              placeholder="ABC123"
              maxLength={6}
              className="tactical-input font-mono tracking-[0.3em] text-center uppercase"
              data-testid="tally-join-code-input"
            />
          </div>
          <Button
            onClick={join}
            disabled={joining}
            variant="outline"
            className="tactical-btn w-full"
            data-testid="tally-join-button"
          >
            {joining ? "Joining..." : "Join Tally"}
          </Button>
          <p className="text-xs text-muted-foreground text-center">
            Ask the host for the 6-letter code — multiple servers can edit at once.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

// ---- Active tally view ----
const TallyBoard = ({ session, onUpdate, onReset, onLeave, isGuest }) => {
  const { code, name, tables } = session;
  const shareUrl = useMemo(() => `${window.location.origin}/execution/tally/${code}`, [code]);

  const copyCode = () => {
    navigator.clipboard.writeText(code);
    toast.success("Code copied");
  };

  const shareLink = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: `Dinner Tally: ${name}`, url: shareUrl });
        return;
      } catch (e) {
        /* user cancelled */
      }
    }
    navigator.clipboard.writeText(shareUrl);
    toast.success("Share link copied");
  };

  // Totals per course
  const totals = useMemo(() => {
    const t = { starter: 0, main: 0, dessert: 0 };
    tables.forEach((row) => {
      if (row.starter) t.starter += 1;
      if (row.main) t.main += 1;
      if (row.dessert) t.dessert += 1;
    });
    return t;
  }, [tables]);

  const totalTables = tables.length;
  const allComplete = totals.starter === totalTables && totals.main === totalTables && totals.dessert === totalTables;

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header / Share bar */}
      <Card className="tactical-card">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="label-tactical">Service</p>
              <h2 className="text-2xl font-bold leading-tight">{name}</h2>
              <p className="text-xs text-muted-foreground mt-1">{totalTables} tables</p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={copyCode}
                className="flex items-center gap-2 px-4 py-2 rounded-sm bg-secondary border border-border hover:border-foreground/30 transition-colors"
                data-testid="tally-code-display"
              >
                <span className="label-tactical">Code</span>
                <span className="font-mono text-2xl font-bold tracking-[0.2em]">{code}</span>
                <Copy className="h-4 w-4 ml-1 text-muted-foreground" />
              </button>
              <Button
                size="sm"
                variant="outline"
                onClick={shareLink}
                data-testid="tally-share-button"
              >
                <Share2 className="h-4 w-4 mr-2" />
                Share
              </Button>
              {!isGuest && (
                <>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={onReset}
                    data-testid="tally-reset-button"
                  >
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Reset
                  </Button>
                  <Button size="sm" variant="ghost" onClick={onLeave} data-testid="tally-leave-button">
                    Leave
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Progress summary */}
      <Card className="tactical-card">
        <CardContent className="p-4">
          <div className="grid grid-cols-3 gap-3" data-testid="tally-totals">
            {COURSE_META.map(({ key, label, icon: Icon }) => {
              const pct = totalTables ? Math.round((totals[key] / totalTables) * 100) : 0;
              return (
                <div key={key} className="p-3 bg-secondary/40 rounded-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className="h-4 w-4" />
                    <p className="label-tactical">{label}</p>
                  </div>
                  <p className="text-3xl font-mono font-bold" data-testid={`tally-total-${key}`}>
                    {totals[key]}
                    <span className="text-base text-muted-foreground"> / {totalTables}</span>
                  </p>
                  <div className="mt-2 h-1.5 w-full bg-background rounded-full overflow-hidden">
                    <div
                      className="h-full bg-accent transition-all"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          {allComplete && (
            <div className="mt-4 p-3 rounded-sm bg-secondary border border-foreground/10 flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5" />
              <p className="text-sm font-semibold">All tables served on every course — service complete</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tables grid */}
      <div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
        data-testid="tally-tables-grid"
      >
        {tables.map((table) => {
          const tableComplete = table.starter && table.main && table.dessert;
          return (
            <Card
              key={table.number}
              className={`tactical-card transition-colors ${
                tableComplete ? "tally-card-complete" : ""
              }`}
              data-testid={`tally-table-${table.number}`}
            >
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="label-tactical">Table</p>
                    <p className="text-3xl font-bold font-mono leading-none">{table.number}</p>
                  </div>
                  {tableComplete && (
                    <span
                      className="suite-badge tally-complete-badge"
                      data-testid={`tally-table-${table.number}-complete`}
                    >
                      Complete
                    </span>
                  )}
                </div>
                <div className="space-y-2">
                  {COURSE_META.map(({ key, label, icon: Icon }) => {
                    const served = table[key];
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => onUpdate(table.number, key, !served)}
                        className={`w-full flex items-center justify-between gap-3 px-3 py-2 rounded-sm border transition-colors text-left ${
                          served ? "tally-toggle-served" : "tally-toggle-pending"
                        }`}
                        data-testid={`tally-toggle-${table.number}-${key}`}
                        aria-pressed={served}
                      >
                        <span className="flex items-center gap-2 text-sm font-medium">
                          <Icon className="h-4 w-4" />
                          {label}
                        </span>
                        {served ? (
                          <CheckCircle2 className="h-5 w-5" />
                        ) : (
                          <Circle className="h-5 w-5 opacity-60" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

// ---- Wrapper ----
export const DinnerTally = () => {
  const { code: urlCode } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const isGuest = !!urlCode && !isAuthenticated;
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(!!urlCode);
  const [error, setError] = useState(null);
  // Track last server-known updated_at to skip no-op state updates
  const lastUpdatedAtRef = useRef(null);
  // Pause polling briefly after a local toggle so the user's interaction wins
  const pausePollUntilRef = useRef(0);

  const applyServerSession = useCallback((data) => {
    // Skip if the server's data is identical (or older than) our last known state
    if (data && data.updated_at && data.updated_at === lastUpdatedAtRef.current) {
      return;
    }
    lastUpdatedAtRef.current = data?.updated_at || null;
    setSession(data);
  }, []);

  const loadSession = useCallback(async () => {
    if (!urlCode) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/tally/sessions/${urlCode}`);
      applyServerSession(res.data);
      setError(null);
    } catch (e) {
      setError("not_found");
    } finally {
      setLoading(false);
    }
  }, [urlCode, applyServerSession]);

  const pollSession = useCallback(async () => {
    if (!urlCode) return;
    // Skip this poll if user just toggled something
    if (Date.now() < pausePollUntilRef.current) return;
    try {
      const res = await axios.get(`${API}/tally/sessions/${urlCode}`);
      applyServerSession(res.data);
    } catch {
      /* swallow transient poll errors — initial load handles real failures */
    }
  }, [urlCode, applyServerSession]);

  // Initial load when URL has a code
  useEffect(() => {
    // eslint-disable-next-line
    if (urlCode) loadSession();
  }, [urlCode, loadSession]);

  // Background polling — silent, only updates when server data actually changes
  useEffect(() => {
    if (!urlCode || !session) return undefined;
    const id = setInterval(pollSession, SYNC_INTERVAL_MS);
    return () => clearInterval(id);
  }, [urlCode, session, pollSession]);

  const handleUpdate = async (tableNumber, course, served) => {
    if (!session) return;
    // Pause server polling for 5 seconds so the user's interaction wins
    pausePollUntilRef.current = Date.now() + 5000;
    // Optimistic update for snappy UX
    setSession((prev) =>
      prev
        ? {
            ...prev,
            tables: prev.tables.map((t) =>
              t.number === tableNumber ? { ...t, [course]: served } : t
            ),
          }
        : prev
    );
    try {
      const res = await axios.put(`${API}/tally/sessions/${session.code}`, {
        table_number: tableNumber,
        course,
        served,
      });
      // Update our cached updated_at so subsequent polls don't trigger a re-render
      lastUpdatedAtRef.current = res.data.updated_at;
      setSession(res.data);
    } catch (e) {
      toast.error("Could not save — try again");
      // Re-sync from server to undo failed optimistic state
      loadSession();
    }
  };

  const handleReset = async () => {
    if (!session) return;
    if (!window.confirm("Clear all course ticks? Everyone connected will see the reset.")) return;
    pausePollUntilRef.current = Date.now() + 5000;
    try {
      const res = await axios.post(`${API}/tally/sessions/${session.code}/reset`);
      lastUpdatedAtRef.current = res.data.updated_at;
      setSession(res.data);
      toast.success("Tally reset");
    } catch (e) {
      toast.error("Could not reset");
    }
  };

  const handleLeave = () => {
    lastUpdatedAtRef.current = null;
    navigate("/execution/tally", { replace: true });
    setSession(null);
  };

  return (
    <Layout
      title="Dinner Service Tally"
      subtitle={isGuest
        ? "You've been invited to a live tally — toggle courses as you serve them"
        : "Track which tables have had which course — sync across servers in real time"}
      guest={isGuest}
    >
      {loading && (
        <div className="text-center py-12 text-muted-foreground">Loading tally...</div>
      )}

      {!loading && error === "not_found" && (
        <div className="max-w-md mx-auto text-center py-12 space-y-4">
          <ClipboardList className="h-12 w-12 mx-auto opacity-50" />
          <p className="text-lg font-semibold">Tally not found</p>
          <p className="text-sm text-muted-foreground">
            The code <span className="font-mono">{urlCode}</span> doesn&apos;t match an active tally.
            {isGuest && " Ask the host to share the link again."}
          </p>
          {!isGuest && (
            <Link to="/execution/tally">
              <Button className="tactical-btn">Start a New Tally</Button>
            </Link>
          )}
        </div>
      )}

      {!loading && !error && !session && (
        <TallySetup onSession={setSession} />
      )}

      {!loading && !error && session && (
        <TallyBoard
          session={session}
          onUpdate={handleUpdate}
          onReset={handleReset}
          onLeave={handleLeave}
          isGuest={isGuest}
        />
      )}
    </Layout>
  );
};

export default DinnerTally;
